import styles from "./FounderCompanyProfilePage.module.scss";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { HeaderSection } from "./HeaderSection/HeaderSection";
import { useEffect, useRef, useState } from "react";
import { useScrollToTop } from "@/frontend/shared/lib/hooks/useScrollToTop";
import { DataRoomSection } from "./DataRoomSection/ui/DataRoomSection";
import useHeight from "@/frontend/shared/lib/hooks/useHeight";
import useScrollProgress from "@/frontend/shared/lib/hooks/useScrollProgress";
import { useSearchParams } from "react-router-dom";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import { ESectionOptions, Sidebar } from "@/frontend/widgets/Sidebar";
import { OverviewSection } from "@/frontend/features/Company/Profile/ui/OverviewSection/OverviewSection";
import { FinancingSection } from "@/frontend/features/Company/Profile/ui/FinancingSection/FinancingSection";
import { TeamSection } from "@/frontend/features/Company/Profile/ui/TeamSection/TeamSection";
import { EditOverviewModal } from "@/frontend/entities/Founder/ui/EditOverviewModal/EditOverviewModal";
import { EEditableSections } from "@/frontend/entities/Founder/model/types/types";
import { MarketOverviewSection } from "@/frontend/features/Company/Profile/ui/MarketOverviewSection/MarketOverviewSection";
import { MarketLandscapeSection } from "@/frontend/features/Company/Profile/ui/MarketLandscapeSection/MarketLandscapeSection";
import { SolutionOverviewSection } from "@/frontend/features/Company/Profile/ui/SolutionOverviewSection/SolutionOverviewSection";
import { EditMarketModal } from "@/frontend/entities/Founder/ui/EditMarketModal/EditMarketModal";
import { EditCompetitorsModal } from "@/frontend/entities/Founder/ui/EditCompetitorsModal/EditCompetitorsModal";
import { EditTeamModal } from "@/frontend/entities/Founder/ui/EditTeamModal/EditTeamModal";
import { getFounderCompanyData } from "../lib/getFounderCompanyData";
import { FounderReportErrorModal } from "@/frontend/entities/Founder/ui/FounderReportErrorModal/FounderReportErrorModal";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { EditSolutionModal } from "@/frontend/entities/Founder/ui/EditSolutionModal/EditSolutionModal";
import { EAccessDataStatus } from "../../ExpertPortfolioCompanyProfilePage/model/types/types";
import { EActiveView } from "@/frontend/features/Company/model/types/types";

export function FounderCompanyProfilePage() {
  const [isEditModalOpen, setIsEditModalOpen] = useState<EEditableSections | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialActiveView = searchParams.get("view");
  const [activeView, setActiveView] = useState<EActiveView>((initialActiveView as EActiveView) || EActiveView.PROFILE);
  const { ref: sidebarRef, height: sidebarHeightFromRef } = useHeight();
  const scrollProgress = useScrollProgress();
  const [isContainerScrolled, setIsContainerScrolled] = useState(true);
  const [isReportAnErrorModalShown, setIsReportAnErrorModalShown] = useState(false);

  useEffect(() => {
    setSearchParams({ view: activeView });
  }, [activeView, setSearchParams]);

  useEffect(() => {
    if (scrollProgress > 15 && isContainerScrolled) {
      setIsContainerScrolled(false);
    } else if (scrollProgress < 10 && !isContainerScrolled) {
      setIsContainerScrolled(true);
    }
  }, [scrollProgress, isContainerScrolled]);

  const {
    detailsData,
    isLoadingDetailsData,
    refetchDetailsData,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    competitorsData,
    isLoadingCompetitorsData,
    refetchCompetitorsData,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    similarCompaniesDataStatus,
    productsDataStatus,
    marketOverviewDataStatus
  } = getFounderCompanyData();

  useScrollToTop();

  const initialSelectedSection = ESectionOptions.OVERVIEW;
  const [selectedSection, setSelectedSection] = useState<ESectionOptions>(initialSelectedSection);

  const { ref: headerRef, height: headerHeight } = useHeight();

  const sectionRefs = {
    [ESectionOptions.OVERVIEW]: useRef(null),
    [ESectionOptions.FINANCING]: useRef(null),
    [ESectionOptions.TEAM]: useRef(null),
    [ESectionOptions.MARKET]: useRef(null),
    [ESectionOptions.PRODUCTS_AND_SERVICES]: useRef(null),
    [ESectionOptions.COMPETITIVE_LANDSCAPE]: useRef(null)
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let mostVisibleSection = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio + 0.1) {
            // 0.1 is a buffer to prevent rapid switching - A.
            maxRatio = entry.intersectionRatio;
            mostVisibleSection = entry.target.getAttribute("data-section");
          }
        });

        if (mostVisibleSection) {
          setSelectedSection(mostVisibleSection);
        }
      },
      {
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
        threshold: 0.01
      }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionRefs, setSelectedSection, headerHeight]);
  const companyName = detailsData?.name;
  const logoUrl = detailsData?.logoUrl;
  const isMobile = useScreenWidth() <= 768;

  const styleMarginTop = isMobile
    ? {}
    : {
        marginTop: `-${sidebarHeightFromRef}px`
      };

  const founderAccess = EAccessDataStatus.FULL_DATA;
  const content = () => {
    switch (true) {
      case isLoadingDetailsData || !detailsData:
        return (
          <div className={styles.container}>
            <InnoveraCompanyHeader />
            <div className={styles.loadingContentContainer}>
              <Loader />
            </div>
          </div>
        );
      case activeView === EActiveView.DATA_ROOM:
        return (
          <div className={styles.container}>
            <InnoveraCompanyHeader />
            <div className={styles.contentContainer}>
              <HeaderSection
                data={detailsData}
                activeView={activeView}
                onChangeActiveView={setActiveView}
                triggerRefetch={refetchDetailsData}
              />
              <DataRoomSection id={detailsData?.id} />
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.container}>
            <div className={styles.headerContainer} ref={headerRef}>
              <div className={styles.headerContent}>
                <InnoveraCompanyHeader />
                <HeaderSection
                  data={detailsData}
                  activeView={activeView}
                  onChangeActiveView={setActiveView}
                  triggerRefetch={refetchDetailsData}
                  isContainerScrolled={isContainerScrolled}
                />
              </div>
              {!isMobile && (
                <div className={styles.sidebarContainer}>
                  <Sidebar
                    selectedSection={selectedSection}
                    sectionRefs={sectionRefs}
                    top={headerHeight}
                    sidebarRef={sidebarRef}
                  />
                </div>
              )}
            </div>
            <div className={styles.contentBlock} style={styleMarginTop}>
              <div className={styles.subContentContainer}>
                {detailsData && (
                  <OverviewSection
                    innoveraData={detailsData.companyData}
                    aiProvidedDescription={detailsData.description || ""}
                    innerRef={sectionRefs[ESectionOptions.OVERVIEW]}
                    onReportError={() => setIsReportAnErrorModalShown(true)}
                    className={styles.overviewSection}
                    logoUrl={logoUrl}
                    companyName={companyName}
                    dataStatusFromAccess={founderAccess}
                    onEdit={() => setIsEditModalOpen(EEditableSections.OVERVIEW)}
                    isLoadingAiProvidedDescription={isLoadingDetailsData}
                    isLoadingInnoveraData={isLoadingDetailsData}
                  />
                )}
                <FinancingSection
                  innerRef={sectionRefs[ESectionOptions.FINANCING]}
                  innoveraData={detailsData?.companyData}
                  onReportError={() => setIsReportAnErrorModalShown(true)}
                  className={styles.financingSection}
                  isLoadingInnoveraData={isLoadingDetailsData}
                />
                <TeamSection
                  innerRef={sectionRefs[ESectionOptions.TEAM]}
                  aiTeamData={detailsData?.employees}
                  teamData={teamData}
                  isLoadingTeamData={isLoadingTeamData}
                  isLoadingAiTeamData={isLoadingDetailsData}
                  onReportError={() => setIsReportAnErrorModalShown(true)}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  dataStatusFromAccess={founderAccess}
                  onEdit={() => setIsEditModalOpen(EEditableSections.TEAM)}
                />
                <MarketOverviewSection
                  innerRef={sectionRefs[ESectionOptions.MARKET]}
                  aiProvidedDescription={detailsData?.marketDescription || ""}
                  marketOverviewData={marketOverviewData}
                  isLoadingAiProvidedDescription={isLoadingDetailsData}
                  isLoadingMarketOverviewData={isLoadingMarketOverviewData}
                  onReportError={() => setIsReportAnErrorModalShown(true)}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  dataStatusFromAccess={founderAccess}
                  onEdit={() => setIsEditModalOpen(EEditableSections.MARKET)}
                  marketOverviewDataStatus={marketOverviewDataStatus}
                />
                <SolutionOverviewSection
                  isLoadingAiProvidedDescription={isLoadingDetailsData}
                  innerRef={sectionRefs[ESectionOptions.PRODUCTS_AND_SERVICES]}
                  productsData={productsData}
                  patentsData={patentsData}
                  isLoadingProducts={isLoadingProductsData}
                  isLoadingPatents={isLoadingPatentsData}
                  aiProvidedDescription={detailsData?.solutionDescription || ""}
                  onReportError={() => setIsReportAnErrorModalShown(true)}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  dataStatusFromAccess={founderAccess}
                  onEdit={() => setIsEditModalOpen(EEditableSections.PRODUCTS)}
                  productsDataStatus={productsDataStatus}
                />
                <MarketLandscapeSection
                  innerRef={sectionRefs[ESectionOptions.COMPETITIVE_LANDSCAPE]}
                  competitors={competitorsData}
                  isLoadingCompetitors={isLoadingCompetitorsData}
                  refetchCompetitors={refetchCompetitorsData}
                  refetchSimilarCompaniesData={refetchSimilarCompaniesData}
                  companyName={companyName}
                  logoUrl={logoUrl}
                  onReportError={() => setIsReportAnErrorModalShown(true)}
                  onEdit={() => setIsEditModalOpen(EEditableSections.COMPETITORS)}
                  isLoadingSimilarCompaniesData={isLoadingSimilarCompaniesData}
                  similarCompaniesData={similarCompaniesData}
                  dataStatusFromAccess={founderAccess}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {content()}
      <EditOverviewModal
        isOpen={isEditModalOpen === EEditableSections.OVERVIEW}
        onClose={() => setIsEditModalOpen(null)}
        aiProvidedDescription={detailsData?.description || ""}
        triggerRefetch={refetchDetailsData}
      />
      <EditSolutionModal
        isOpen={isEditModalOpen === EEditableSections.PRODUCTS}
        onClose={() => setIsEditModalOpen(null)}
        aiProvidedDescription={detailsData?.solutionDescription || ""}
        triggerRefetch={refetchDetailsData}
      />
      <EditCompetitorsModal
        isOpen={isEditModalOpen === EEditableSections.COMPETITORS}
        onClose={() => setIsEditModalOpen(null)}
        competitors={competitorsData || []}
        triggerRefetch={refetchCompetitorsData}
      />
      <EditMarketModal
        isOpen={isEditModalOpen === EEditableSections.MARKET}
        onClose={() => setIsEditModalOpen(null)}
        aiProvidedDescription={detailsData?.marketDescription || ""}
        triggerRefetch={refetchDetailsData}
      />
      <EditTeamModal
        isOpen={isEditModalOpen === EEditableSections.TEAM}
        onClose={() => setIsEditModalOpen(null)}
        teamData={detailsData?.employees || []}
        triggerRefetch={refetchDetailsData}
      />
      <FounderReportErrorModal isOpen={isReportAnErrorModalShown} onClose={() => setIsReportAnErrorModalShown(false)} />
    </>
  );
}
